import type { ClipboardEvent } from 'react'
import type { CellReference, CellSegment } from '../schemas'
import { tableReferenceLabel } from '../reference'
import { matterPaths } from '@/routing/paths'

export function copySourceLabel(reference: CellReference, matterId?: string, tableId?: string): string {
  const label = tableReferenceLabel(reference)
  // Match Excel exports: open the saved citation without embedding its excerpt in the URL.
  const hasSource = reference.document_id || reference.agency_table_id || reference.docket_table_id || (reference.legal_kind && reference.legal_path)
  let href: string | null = null
  if (reference.web_url) {
    if (/^https?:\/\//i.test(reference.web_url)) href = reference.web_url
  } else if (matterId && tableId && hasSource) {
    href = `${matterPaths.table(matterId, tableId)}?${new URLSearchParams({ reference: reference.id })}`
  }
  return href ? `${label} — ${new URL(href, window.location.origin).href}` : `${label} (source unavailable)`
}

export function numberedCopyText(segments: CellSegment[], sources: Map<string, string>): string {
  const citedSegments = segments.filter((segment) => segment.reference_ids.length > 0)
  const singleCitation = citedSegments.length === 1 && citedSegments[0].reference_ids.length === 1 ? citedSegments[0] : undefined
  if (
    singleCitation &&
    segments.some((segment) => segment.text.trim()) &&
    segments.slice(segments.indexOf(singleCitation) + 1).every((segment) => !segment.text.trim())
  ) {
    const text = segments.map((segment) => segment.text).join('')
    return `${text}\n\n${sources.get(singleCitation.reference_ids[0]) ?? 'Source unavailable'}`
  }
  const numbers = new Map<string, number>()
  const text = segments
    .map((segment) => {
      const markers = segment.reference_ids
        .map((id) => {
          if (!numbers.has(id)) numbers.set(id, numbers.size + 1)
          return ` [${numbers.get(id)}]`
        })
        .join('')
      return segment.text + markers
    })
    .join('')
  const notes = [...numbers].map(([id, number]) => `[${number}] ${sources.get(id) ?? 'Source unavailable'}`)
  return notes.length ? `${text}\n\n${notes.join('\n')}` : text
}

/** Preserve only selected prose; copy selected citations atomically with their source metadata. */
export function copyTableSelection(event: ClipboardEvent<HTMLElement>): void {
  if (event.defaultPrevented) return
  const selection = window.getSelection()
  if (!selection || selection.isCollapsed || !selection.rangeCount) return
  const range = selection.getRangeAt(0)
  const rows = new Map<Element, string[]>()
  const root = event.currentTarget
  for (const cell of root.querySelectorAll<HTMLElement>('[data-table-copy-content]')) {
    if (!range.intersectsNode(cell)) continue
    const part = document.createRange()
    part.selectNodeContents(cell)
    if (range.compareBoundaryPoints(Range.START_TO_START, part) > 0) part.setStart(range.startContainer, range.startOffset)
    if (range.compareBoundaryPoints(Range.END_TO_END, part) < 0) part.setEnd(range.endContainer, range.endOffset)
    if (part.collapsed) continue
    const fragment = part.cloneContents()
    const segments: CellSegment[] = []
    const sources = new Map<string, string>()
    let text = ''
    const visit = (node: Node) => {
      if (node instanceof Element && node.hasAttribute('data-table-copy-ignore')) return
      if (node instanceof HTMLElement && node.dataset.tableReferenceId) {
        const id = node.dataset.tableReferenceId
        sources.set(id, node.dataset.tableReferenceSource ?? 'Source unavailable')
        segments.push({ text, reference_ids: [id] })
        text = ''
      } else if (node.nodeType === Node.TEXT_NODE) text += node.textContent ?? ''
      else if (node instanceof HTMLBRElement) text += '\n'
      else node.childNodes.forEach(visit)
    }
    const ancestor = part.commonAncestorContainer
    const selectedReference = (ancestor instanceof Element ? ancestor : ancestor.parentElement)?.closest<HTMLElement>('[data-table-reference-id]')
    if (selectedReference) visit(selectedReference)
    else visit(fragment)
    if (text) segments.push({ text, reference_ids: [] })
    const row = cell.closest('tr') ?? root
    const values = rows.get(row) ?? []
    values.push(numberedCopyText(segments, sources))
    rows.set(row, values)
  }
  if (!rows.size) return
  const values = [...rows.values()]
  const singleCell = values.length === 1 && values[0].length === 1
  const quote = (value: string) => (/[\t\n"]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value)
  const text = singleCell ? values[0][0] : values.map((row) => row.map(quote).join('\t')).join('\n')
  const html = document.createElement(singleCell ? 'div' : 'table')
  if (singleCell) {
    html.style.whiteSpace = 'pre-wrap'
    html.textContent = text
  } else {
    for (const row of values) {
      const tr = document.createElement('tr')
      for (const value of row) {
        const td = document.createElement('td')
        td.style.whiteSpace = 'pre-wrap'
        td.textContent = value
        tr.append(td)
      }
      html.append(tr)
    }
  }
  event.clipboardData.setData('text/plain', text)
  event.clipboardData.setData('text/html', html.outerHTML)
  event.preventDefault()
}
